import React from 'react';
import PurchaseOrderUserInfo from './PurchaseOrderUserInfo';
import PurchaseOrderPriceInfo from './PurchaseOrderPriceInfo';
import PurchaseOrderDeliveryInfo from './PurcahseOrderDeliveryInfo';
import PurchaseOrderMoldTransferInfo from './PurchaseOrderMoldTransferInfo';
import PurchaseOrderDraftEditCondition from './PurchaseOrderDraftEditCondition';
import { Divider, makeStyles } from '@material-ui/core';
import { colorSet } from '@components/Provider';
import { CapaQuotationServiceType } from '@generated/graphql';
import PurchaseOrderTitle from './PurchaseOrderTitle';
import PurchaseOrderTotalPrice from './PurchaseOrderTotalPrice';
import PurchaseOrderPaymentInfo from './PurchaseOrderPaymentInfo';
import PurchaseOrderInspectionInfo from './PurchaseOrderInspectionInfo';
import { PurchaseOrderItemsTable } from '../Common';
import { ProgressInfoCard } from './ProgressInfoBox';
import { PurchaseOrderViewPresenterProps } from './types';

const DESIGN_INCLUDED_SERVICE_TYPES: (
  | CapaQuotationServiceType
  | undefined
)[] = [CapaQuotationServiceType.Design, CapaQuotationServiceType.Pcb];

const PurchaseOrderViewPresenter: React.FC<PurchaseOrderViewPresenterProps> = ({
  matches,
  selectedPurchaseOrder,
  projectName,
  payment,
  shipping,
  hasPaymentPlanInfo,
  hasInspectionInfo,
  serviceType,
  isHistory = false
}) => {
  const classes = usePurchaseOrderViewPresenterStyles();

  const items = selectedPurchaseOrder?.items ?? [];
  const pcbFabricationItems = selectedPurchaseOrder?.pcbFabricationItems ?? [];
  const pcbAssemblyItems = selectedPurchaseOrder?.pcbAssemblyItems ?? [];

  const haveFabricationItems = pcbFabricationItems.length !== 0;
  const haveAssemblyItems = pcbAssemblyItems.length !== 0;
  const haveNormalItems = items.length !== 0;

  const isDesign =
    DESIGN_INCLUDED_SERVICE_TYPES.includes(serviceType) &&
    !haveAssemblyItems &&
    !haveFabricationItems;

  return (
    <div className={classes.container} id="scroll">
      {!isHistory && (
        <ProgressInfoCard
          purchaseOrder={selectedPurchaseOrder}
          payment={payment}
          shipping={shipping}
          processStatus={selectedPurchaseOrder?.status}
        />
      )}

      <PurchaseOrderTitle
        projectName={projectName}
        currentItem={selectedPurchaseOrder}
      />

      {isDesign && (
        <PurchaseOrderPriceInfo
          isDesign={isDesign}
          currentItem={selectedPurchaseOrder}
        />
      )}

      {haveNormalItems && !isDesign && (
        <PurchaseOrderItemsTable projectName={projectName} items={items} />
      )}

      {haveFabricationItems && (
        <PurchaseOrderItemsTable
          projectName={projectName}
          items={pcbFabricationItems}
        />
      )}

      {haveAssemblyItems && (
        <PurchaseOrderItemsTable
          projectName={projectName}
          items={pcbAssemblyItems}
        />
      )}

      {matches && <Divider />}

      {/* 총 발주금액 */}
      <PurchaseOrderTotalPrice
        quotationPrice={selectedPurchaseOrder?.quotation ?? 0}
        deliveryCost={selectedPurchaseOrder?.deliveryCost ?? 0}
      />

      <Divider />

      {/* 납품 정보 */}
      <PurchaseOrderDeliveryInfo currentItem={selectedPurchaseOrder} />

      <Divider />

      {/* 최대 수정시안조건 */}
      <PurchaseOrderDraftEditCondition
        currentItem={selectedPurchaseOrder}
        renderDivider={() => <Divider />}
      />

      {/* 검수 조건. not-null이었으나, 결제기능 추가로 nuallble로 변경됨. */}
      {hasInspectionInfo && (
        <>
          <PurchaseOrderInspectionInfo currentItem={selectedPurchaseOrder} />
          <Divider />
        </>
      )}

      {/* 주형 및 금형 이관 조건 */}
      <PurchaseOrderMoldTransferInfo
        currentItem={selectedPurchaseOrder}
        serviceType={serviceType}
        renderDivider={() => <Divider />}
      />

      {/* 결제 정보. not-null이었으나 결제기능 추가로 nuallble로 변경됨. */}
      {hasPaymentPlanInfo && (
        <>
          <PurchaseOrderPaymentInfo currentItem={selectedPurchaseOrder} />
          <Divider />
        </>
      )}

      {/* 파트너(수주처) 정보 */}
      <PurchaseOrderUserInfo
        userType="receiver"
        currentItem={selectedPurchaseOrder}
      />
      <Divider />

      {/* 내(발주처) 정보 */}
      <PurchaseOrderUserInfo
        userType="publish"
        currentItem={selectedPurchaseOrder}
      />
    </div>
  );
};

export default PurchaseOrderViewPresenter;

const usePurchaseOrderViewPresenterStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    alignSelf: 'center',
    maxWidth: '600px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: colorSet.gray000,
    height: 'calc(100%)',
    overflowX: 'visible',
    padding: '32px 0px 0px',
    '& > hr.MuiDivider-root': {
      height: 1,
      width: 552,
      background: colorSet.gray400,
      [theme.breakpoints.down('md')]: {
        width: '100%',
        height: 16,
        background: colorSet.gray200
      }
    },
    '& .history': {
      maxWidth: 600,
      width: '100%',
      padding: '0px 24px',
      [theme.breakpoints.down('md')]: {
        padding: '0px 16px'
      }
    }
  }
}));
